import { baseButtonDefaults, baseActorDefaults } from "./BaseDeviceProps";
import DeviceTemplate from "./DeviceTemplate";
import BaseModel from "./BaseModel";
import { copyAllProperties } from "./utils";
import BaseModelMap from "./BaseModelMap";
import TemplateGroupTreeViewModel from "../modelTreeViews/TemplateGroupTreeviewModel";
import { DeviceClass, DeviceClasses } from "./DeviceClasses";
import BaseObject from "./BaseObject";

const logger = new Logger();
//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import Logger from "../Logger";
import { itx } from "../translationItx/itx";

ObjectFactory.registerClass("TemplateGroup", (data?: any, parent?: BaseModel) => {
  return new TemplateGroup(data, parent);
});
//////////////////////////////////////////////////
export default class TemplateGroup extends BaseModel {
  public get className(): string {
    return "TemplateGroup"; // no itx
  }

  // public class properties

  templates = new BaseModelMap<DeviceTemplate>(DeviceTemplate, this, "t");
  templateGroups = new BaseModelMap<TemplateGroup>(TemplateGroup, this, "g");

  getParentCollectionName(): string {
    return "templateGroups";
  }

  // -----------------------------------------------------------------------------
  // templateGroupType
  // -----------------------------------------------------------------------------
  _templateGroupType: DeviceClass = DeviceClasses.Unknown;
  get templateGroupType(): DeviceClass {
    return this._templateGroupType;
  }

  set templateGroupType(newValue: DeviceClass) {
    if (this._templateGroupType === newValue) return;
    this.beforeChange("templateGroupType");
    this._templateGroupType = newValue;
    this.changed("templateGroupType");
  }

  // -----------------------------------------------------------------------------

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    this.treeViewModel = new TemplateGroupTreeViewModel(this);
    copyAllProperties(data, this);
    // Nested groups keep their own names and tags instead of the fixed root category labels.
    if (parent instanceof TemplateGroup || data?.tag === "C1") return;
    switch (this.templateGroupType) {
      case DeviceClasses.Actor:
        this.name =
          state.translate("components.plural.Actor", "Aktoren") +
          " " +
          itx("&", "&") +
          " " +
          state.translate("components.CabinetElement", "Schaltschrankelemente");
        this.tag = "01";
        return;

      case DeviceClasses.Button:
        this.name = state.translate("components.plural.Button", "Buttons");
        this.tag = "02";
        return;

      case DeviceClasses.SensorNew:
        this.name = state.translate("components.plural.SensorNew", "Sensoren");
        this.tag = "03";
        return;
      case DeviceClasses.DecentralActor:
        this.name = state.translate("components.plural.DecentralActor", "Dezentrale Aktoren");
        this.tag = "07";
        return;
      // case DeviceClasses.Gateway:
      //   this.name = state.translate("components.plural.Gateway", "Gateways");
      //   this.tag = "04";
      //   return;

      // case DeviceClasses.Other:
      //   this.name = state.translate("components.plural.Other", "Other");
      //   this.tag = "05";
      //   return;

      // case DeviceClasses.ButtonActor:
      //   this.name = state.translate("components.plural.ButtonActor", "Taster Aktoren");
      //   this.tag = "06";
      //   return;

      case DeviceClasses.Unknown:
        ((this.name = state.translate("components.plural.Unspecified", "Nicht spezifiziert")), (this.tag = "00"));
        return;
    }
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;
    var res = new Array<BaseObject>();

    // delete all templates
    res = res.concat(this.templates.deleteAllItems());
    res = res.concat(this.templateGroups.deleteAllItems());

    // delete this
    res = res.concat(super.delete());
    return res;
  }

  // save & load

  getPersistentProperties(): string[] {
    return ["templateGroupType", "templates", "templateGroups", ...super.getPersistentProperties()]; // no itx
  }
  ////////////////////////////////////////

  canBeDeletedInteractive(): boolean {
    return false;
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  canBeCopiedInteractive(): boolean {
    return false;
  }

  templateAlreadyInGroup(newTemplate: DeviceTemplate): boolean {
    let res = false;
    this.templates.forEach((temp) => {
      if (temp?.knxProductData?.modelDataId == newTemplate.knxProductData.modelDataId) {
        res = true;
      }
    });
    return res;
  }

  /** Includes nested product selections in the application's template lookup. */
  allTemplates(): DeviceTemplate[] {
    const templates = this.templates.sortBy("name");
    for (const group of this.templateGroups.sortBy("tag")) templates.push(...group.allTemplates());
    return templates;
  }

  /** Preferred template for this category, including nested categories and the first-template fallback. */
  get favoriteTemplate(): DeviceTemplate | undefined {
    const templates = this.allTemplates();
    return templates.find(template => String(template.tag).trim() === "0") ?? templates[0];
  }

  /** Explicit favorites belong to their immediate group; sibling categories remain independent. */
  set favoriteTemplate(template: DeviceTemplate | undefined) {
    const templates = this.templates.asArray();
    if (template && !templates.includes(template)) return;
    state.undoManager.runAsSingleStep(itx("templateGroup.favorite", "Favoriten festlegen"), () => {
      for (const sibling of templates) {
        if (sibling !== template && String(sibling.tag).trim() === "0") sibling.tag = "";
      }
      if (template) template.tag = "0";
      this.raiseOnChange("favoriteTemplate");
    });
  }

  ///// for paste
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;
    if (item.className == "DeviceTemplate") {
      return await item.executePaste({
        parent: this,
        isCopyRootObj: true,
      });
    }
    return [];
  }

  consistencyCheck(parent: BaseModel) {
    this.templateGroups.consistencyCheck(this);
    this.templates.forEach((template) => {
      template.consistencyCheck(this);
    });
  }
}

export type ProductCategoryKey = "buttons" | "actors" | "sensors";
type CategoryDefinition = {
  tag: string;
  legacyTag: string;
  name: string;
  type: DeviceClass;
  segments: Record<string, { tag: string; name: string; subType: string }>;
};
export const productTemplateCategories: Record<ProductCategoryKey, CategoryDefinition> = {
  sensors: { tag: "03", legacyTag: "S", name: "Sensoren", type: DeviceClasses.SensorNew, segments: {} },
  actors: {
    tag: "01",
    legacyTag: "A",
    name: "Aktoren",
    type: DeviceClasses.Actor,
    segments: {
      dimming: { tag: "dim", subType: "Dimming", name: "Dimmen" },
      switching: { tag: "switch", subType: "Switching", name: "Schalten" },
      shading: { tag: "shading", subType: "Shading", name: "Jalousie / Rollo" },
      heating: { tag: "heating", subType: "Heating", name: "Heizen" },
      binaryInputs: { tag: "binaryInputs", subType: "BinaryInputs", name: "Binäreingänge" },
    },
  },
  buttons: {
    tag: "02",
    legacyTag: "T",
    name: "Taster",
    type: DeviceClasses.Button,
    segments: {
      simple: { tag: "simple", subType: "Simple", name: "Einfach" },
      touch: { tag: "touch", subType: "Touch", name: "Touch" },
      panel: { tag: "panel", subType: "Panel", name: "Panel" },
    },
  },
};

/** Adds a missing group without replacing existing templates or user customizations. */
function ensureGroup(
  collection: BaseModelMap<TemplateGroup>,
  tag: string,
  name: string,
  type: DeviceClass,
): TemplateGroup {
  const existing = collection.findFirst((group) => group.tag === tag);
  if (existing) return existing;
  const parent = collection.parent instanceof TemplateGroup ? collection.parent : collection;
  const group = new TemplateGroup({ tag, name, templateGroupType: type }, parent);
  collection.add(group);
  return group;
}

/** Merges legacy branches without recreating templates or breaking device references. */
function mergeGroup(source: TemplateGroup, target: TemplateGroup): void {
  for (const template of source.templates.asArray()) {
    source.templates.remove(template);
    template.parent = target;
    target.templates.add(template);
  }
  for (const child of source.templateGroups.asArray()) {
    const existing = target.templateGroups.findFirst((group) => group.tag === child.tag);
    if (existing) {
      mergeGroup(child, existing);
    } else {
      source.templateGroups.remove(child);
      child.parent = target;
      target.templateGroups.add(child);
    }
  }
  source.delete();
}

/** Uses the standard top-level categories and migrates the former ConceptOne wrapper. */
export function ensureProductTemplateGroups(
  collection: BaseModelMap<TemplateGroup>,
  createGenericDefaults = false,
): BaseModelMap<TemplateGroup> {
  const legacyRoot = collection.findFirst((group) => group.tag === "C1");
  for (const [key, definition] of Object.entries(productTemplateCategories)) {
    const legacyCategory = legacyRoot?.templateGroups.findFirst((group) => group.tag === definition.legacyTag);
    let category = collection.findFirst((group) => group.tag === definition.tag);
    if (legacyCategory) {
      if (category) {
        mergeGroup(legacyCategory, category);
      } else {
        legacyRoot!.templateGroups.remove(legacyCategory);
        legacyCategory.parent = collection;
        legacyCategory.tag = definition.tag;
        collection.add(legacyCategory);
        category = legacyCategory;
      }
    }
    category ??= ensureGroup(collection, definition.tag, definition.name, definition.type);
    for (const segment of Object.values(definition.segments)) {
      const group = ensureGroup(category.templateGroups, segment.tag, segment.name, definition.type);
      if (createGenericDefaults && group.templates.size === 0 && group.templateGroups.size === 0) {
        const params = key === "buttons"
          ? { buttonBaseProps: baseButtonDefaults(segment.subType) }
          : { actorBaseProps: baseActorDefaults(segment.subType) };
        DeviceTemplate.createGeneric(group, params, `Generisch: ${segment.name}`,
          key === "buttons" ? "Button" : "Actor");
      }
    }
    if (createGenericDefaults && key === "sensors" && category.templates.size === 0 && category.templateGroups.size === 0) {
      DeviceTemplate.createGeneric(category, { actorBaseProps: baseActorDefaults() }, "Generischer Sensor", "Sensor");
    }
  }
  if (legacyRoot && legacyRoot.templates.size === 0 && legacyRoot.templateGroups.size === 0) legacyRoot.delete();
  return collection;
}

/** Shared initialization for C1 projects and initially empty main-app product collections. */
export function createMissingTemplateGroups(
  collection: BaseModelMap<TemplateGroup> = state.templateGroups,
  isC1Project = globalThis.state?.projectMetadata?.isC1Project ?? false,
): BaseModelMap<TemplateGroup> {
  if (isC1Project || collection.size === 0) ensureProductTemplateGroups(collection, true);
  const completeTemplateGroups = new Map<DeviceClass, { name: string; tag: string }>();
  completeTemplateGroups.set(DeviceClasses.Unknown, {
    name: state.translate("components.plural.Unspecified", "Nicht spezifiziert"),
    tag: "00",
  });
  completeTemplateGroups.set(DeviceClasses.Actor, {
    name:
      state.translate("components.plural.Actor", "Aktoren") +
      " & " +
      itx("ServerTemplateList.CabinetElements", "Schaltschrankelemente"),
    tag: "01",
  });
  completeTemplateGroups.set(DeviceClasses.Button, {
    name: state.translate("components.plural.Button", "Buttons"),
    tag: "02",
  });
  completeTemplateGroups.set(DeviceClasses.SensorNew, {
    name: state.translate("components.plural.SensorNew", "Sensoren"),
    tag: "03",
  });
  completeTemplateGroups.set(DeviceClasses.DecentralActor, {
    name: state.translate("components.plural.DecentralActor", "Dezentrale Aktoren"),
    tag: "07",
  });
  // completeTemplateGroups.set(DeviceClasses.Gateway, {
  //   name: state.translate("components.plural.Gateway", "Gateways"),
  //   tag: "04",
  // });
  // completeTemplateGroups.set(DeviceClasses.Other, {
  //   name: state.translate("components.plural.Other", "Other"),
  //   tag: "05",
  // });
  // completeTemplateGroups.set(DeviceClasses.ButtonActor, {
  //   name: state.translate("components.plural.ButtonActor", "Taster Aktoren"),
  //   tag: "06",
  // });
  for (const [type, { name, tag }] of completeTemplateGroups) {
    const existing = collection.findFirst(group => group.tag !== "C1" && group.templateGroupType === type);
    if (!existing) ensureGroup(collection, tag, name, type);
  }
  return collection;
}

export function getTemplateGroup(name: string): TemplateGroup | undefined {
  createMissingTemplateGroups();

  const xx = state.templateGroups.findAsArray((item) => {
    return item.templateGroupType.name.includes(name);
  });

  if (xx.length >= 1) {
    // because of decentral actor and actor but has to be include because of sensor
    return xx[0];
  }

  return undefined;
}
